// pages/CheckoutAdmin.jsx (or components/CheckoutAdmin.jsx)
import React, { useState, useEffect } from 'react';
import CheckoutPage from '../components/CheckoutPage';
import axios from "axios";
import config from '../config/config';

const CheckoutAdmin = () => {
  const [activeDesign, setActiveDesign] = useState('side-by-side');
  const [title, setTitle] = useState('Checkout');
  const [shippingTitle, setShippingTitle] = useState('Shipping Information');
  const [paymentTitle, setPaymentTitle] = useState('Payment Method');
  const [reviewTitle, setReviewTitle] = useState('Review Your Order');
  const [emptyCartMessage, setEmptyCartMessage] = useState('Your cart is empty');
  const [continueShoppingText, setContinueShoppingText] = useState('Continue Shopping');
  const [checkoutText, setCheckoutText] = useState('Complete Order');
  const [devMode, setDevMode] = useState(false);

  const [background, setBackground] = useState('bg-gray-50');
  const [textColor, setTextColor] = useState('text-gray-900');
  const [buttonBg, setButtonBg] = useState('bg-gray-900');
  const [buttonText, setButtonText] = useState('text-white');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const load = async () => {
      if (!token) return setLoading(false);

      try {
        setLoading(true);
        await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        const res = await axios.get(config.API_URL + '/api/pullCheckoutSettings', {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });

        const db = res.data.checkoutSettings || {};

        setActiveDesign(db.activeDesign || 'side-by-side');
        setTitle(db.title || 'Checkout');
        setShippingTitle(db.shippingTitle || 'Shipping Information');
        setPaymentTitle(db.paymentTitle || 'Payment Method');
        setReviewTitle(db.reviewTitle || 'Review Your Order');
        setEmptyCartMessage(db.emptyCartMessage || 'Your cart is empty');
        setContinueShoppingText(db.continueShoppingText || 'Continue Shopping');
        setCheckoutText(db.checkoutText || 'Complete Order');
        setDevMode(db.devMode || false);

        const styles = db.customStyles || {};
        setBackground(styles.background || 'bg-gray-50');
        setTextColor(styles.textColor || 'text-gray-900');
        setButtonBg(styles.buttonBg || 'bg-gray-900');
        setButtonText(styles.buttonText || 'text-white');
      } catch (err) {
        console.error("Load failed:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  const currentConfig = {
    activeDesign,
    title,
    shippingTitle,
    paymentTitle,
    reviewTitle,
    emptyCartMessage,
    continueShoppingText,
    checkoutText,
    devMode,
    customStyles: { background, textColor, buttonBg, buttonText },
  };

  const save = async () => {
    try {
      setSaving(true);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrf = document.cookie.split('; ').find(r => r.startsWith('XSRF-TOKEN='))?.split('=')[1];

      await axios.post(config.API_URL + "/api/saveCheckoutSettings", { settings: currentConfig }, {
        headers: {
          "Content-Type": "application/json",
          "X-XSRF-TOKEN": decodeURIComponent(xsrf || ''),
        },
        withCredentials: true,
      });

      alert("Checkout settings saved! 🎉");
    } catch (err) {
      alert("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <div className="flex-1 p-8">
        <h1 className="text-3xl font-bold mb-8">Checkout Page Editor</h1>

        {/* Design Selector */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Choose Design</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { value: 'side-by-side', label: 'Side-by-Side', desc: 'Desktop classic' },
              { value: 'wizard', label: 'Multi-Step Wizard', desc: 'Guided flow' },
              { value: 'compact', label: 'Compact Mobile', desc: 'Simple & fast' },
            ].map(d => (
              <button
                key={d.value}
                onClick={() => setActiveDesign(d.value)}
                className={`p-6 border-2 rounded-xl text-left transition-all ${
                  activeDesign === d.value ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <h3 className="font-medium text-lg">{d.label}</h3>
                <p className="text-sm text-gray-600 mt-1">{d.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => setPreviewOpen(true)}
          className="bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700"
        >
          Preview Checkout Page
        </button>
      </div>

      {/* Settings Sidebar */}
      <div className="w-96 bg-white border-l p-6 overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">Customize Checkout</h2>

        <div className="space-y-6">
          <div>
            <label className="block font-medium mb-2">Page Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full p-3 border rounded-lg" />
          </div>

          <div>
            <label className="block font-medium mb-2">Shipping Section Title</label>
            <input value={shippingTitle} onChange={e => setShippingTitle(e.target.value)} className="w-full p-3 border rounded-lg" />
          </div>

          {/* Add more fields for paymentTitle, reviewTitle, etc. */}

          {/* Dev Mode */}
          <div className="bg-yellow-50 p-5 rounded-xl border border-yellow-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Custom Styling</h3>
              <button onClick={() => setDevMode(!devMode)} className={`w-11 h-6 rounded-full relative ${devMode ? 'bg-yellow-600' : 'bg-gray-300'}`}>
                <span className={`absolute w-4 h-4 bg-white rounded-full transition ${devMode ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {devMode && (
              <div className="space-y-4">
                <input value={background} onChange={e => setBackground(e.target.value)} placeholder="Background" className="w-full p-3 border rounded" />
                <input value={textColor} onChange={e => setTextColor(e.target.value)} placeholder="Text Color" className="w-full p-3 border rounded" />
                <input value={buttonBg} onChange={e => setButtonBg(e.target.value)} placeholder="Button BG" className="w-full p-3 border rounded" />
                <input value={buttonText} onChange={e => setButtonText(e.target.value)} placeholder="Button Text" className="w-full p-3 border rounded" />
              </div>
            )}
          </div>

          <button
            onClick={save}
            disabled={saving}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 disabled:opacity-60 mt-8"
          >
            {saving ? 'Saving...' : 'Save Checkout Settings'}
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-auto pt-10">
          <div className="bg-white w-full max-w-6xl rounded-xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold">Checkout Preview</h2>
              <button onClick={() => setPreviewOpen(false)} className="text-3xl text-gray-600 hover:text-gray-900">×</button>
            </div>
            <div className="p-6">
              <CheckoutPage config={currentConfig} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutAdmin;